import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { zodResolver } from '@hookform/resolvers/zod';

import { z } from 'zod';

import { usePostChangePwApi } from '@/api/auth';
import Field from '@/components/common/Field/Field';
import InputText from '@/components/common/Input/InputText/InputText';
import { useToastStore } from '@/store/toastStore';

import * as S from './style';

interface RegisterField {
	originalPassword: string;
	password: string;
	passwordCheck: string;
}

const ModifyPassword = () => {
	const { t } = useTranslation();
	const { openToast } = useToastStore();

	const userRegisterSchema = z
		.object({
			originalPassword: z.string().min(1, { message: 'mypage.password.validation.originalRequired' }),
			password: z
				.string()
				.min(1, { message: 'common.validation.passwordFormat' })
				.min(6, { message: 'common.validation.passwordFormat' })
				.max(20, { message: 'common.validation.passwordFormat' })
				.regex(/^[A-Za-z0-9!@#$%^&*()-_+=]+$/, { message: 'common.validation.passwordFormat' }),
			passwordCheck: z.string().min(1, { message: 'common.validation.passwordMismatch' }),
		})
		.refine((data) => data.password === data.passwordCheck, {
			message: 'common.validation.passwordMismatch',
			path: ['passwordCheck'],
		});

	const {
		register,
		handleSubmit,
		watch,
		formState: { errors },
	} = useForm<RegisterField>({
		resolver: zodResolver(userRegisterSchema),
		mode: 'onChange',
		defaultValues: {
			originalPassword: '',
			password: '',
			passwordCheck: '',
		},
	});

	const { mutateAsync: changePw } = usePostChangePwApi();

	const onSubmit = async () => {
		const res = await changePw({
			member_new_password: watch('password'),
			member_original_password: watch('originalPassword'),
		});
		if (res.data.success) {
			openToast({
				subContent: 'common.societyName',
				content: 'common.toast.infoUpdated',
			});
		} else {
			if (res.data.error.code === '90') {
				openToast({
					subContent: 'common.societyName',
					content: 'mypage.password.toast.originalMismatch',
				});
			}
		}
	};

	return (
		<S.FormContainer onSubmit={handleSubmit(onSubmit)}>
			<S.FieldBox>
				<S.Title>{t('mypage.password.title')}</S.Title>
				<Field title="mypage.password.fields.original" error={errors.originalPassword?.message ?? ''}>
					<InputText
						isError={!!errors.originalPassword}
						type="password"
						placeholder="mypage.password.placeholders.original"
						{...register('originalPassword')}
					/>
				</Field>
				<Field title="mypage.password.fields.new" error={errors.password?.message ?? ''}>
					<InputText type="password" isError={!!errors.password} placeholder="common.placeholders.password" {...register('password')} />
				</Field>
				<Field title="mypage.password.fields.confirm" error={errors.passwordCheck?.message ?? ''}>
					<InputText type="password" isError={!!errors.passwordCheck} {...register('passwordCheck')} placeholder="common.placeholders.passwordConfirm" />
				</Field>
			</S.FieldBox>
			<S.NextButton type="submit">{t('common.actions.completeUpdate')}</S.NextButton>
		</S.FormContainer>
	);
};

export default ModifyPassword;
